import { useEffect, useState } from 'react'
import { apiClient } from '../../services/api'
import Button from '../../components/ui/Button'
import { ui } from '../../styles/ui'

const WORKLOADS = ['receipt_fast', 'receipt_slow', 'long_receipt', 'qr_selector', 'speech_receipt', 'translation_ui', 'translation_category', 'product_categorization']
const emptyRoutes = () => Object.fromEntries(WORKLOADS.map((key) => [key, { primaryEngineId: '', fallbackEngineIds: [], automaticFallback: false }]))

export default function LlmRoutingAdminPanel({ engines }) {
  const [state, setState] = useState(null)
  const [routes, setRoutes] = useState(emptyRoutes)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  useEffect(() => { apiClient.get('/admin/llm-routing').then(({ data }) => {
    setState(data.data)
    if (data.data.activeRoutes) setRoutes(data.data.activeRoutes)
  }).catch(() => setMessage('Routing revision history is unavailable')) }, [])

  const edit = (workload, field, value) => setRoutes((current) => ({ ...current, [workload]: { ...current[workload], [field]: value } }))
  const save = async () => {
    setBusy(true); setMessage('')
    try {
      const payload = Object.fromEntries(WORKLOADS.map((workload) => [workload, {
        ...routes[workload], fallbackEngineIds: routes[workload].automaticFallback ? routes[workload].fallbackEngineIds : [],
      }]))
      const response = await apiClient.put('/admin/llm-routing', { routes: payload, reason })
      setMessage(`Activated routing revision ${response.data.data.revision}`)
      setReason('')
      const refreshed = await apiClient.get('/admin/llm-routing')
      setState(refreshed.data.data); setRoutes(refreshed.data.data.activeRoutes || payload)
    } catch (error) { setMessage(error.response?.data?.message || 'Routing activation failed') }
    finally { setBusy(false) }
  }
  const restore = async (revision) => {
    setBusy(true); setMessage('')
    try {
      const response = await apiClient.post(`/admin/llm-routing/${revision}/restore`, { reason })
      setMessage(`Restored as routing revision ${response.data.data.revision}`)
      const refreshed = await apiClient.get('/admin/llm-routing'); setState(refreshed.data.data); setRoutes(refreshed.data.data.activeRoutes)
    } catch (error) { setMessage(error.response?.data?.message || 'Routing restore failed') }
    finally { setBusy(false) }
  }

  return <section className={ui.card} aria-label="Versioned LLM routing">
    <div className={ui.cardBody}>
      <h2 className="text-lg font-semibold">Versioned workload routing</h2>
      <p className={ui.subtitle}>Every workload requires an explicit active primary. Fallback order is preserved and activation is audited.</p>
      {WORKLOADS.map((workload) => <div key={workload} className="grid gap-2 border-b py-3 md:grid-cols-4">
        <strong>{workload}</strong>
        <label className={ui.label}>Primary<select className={'mt-1 ' + ui.select} value={routes[workload]?.primaryEngineId || ''} onChange={(event) => edit(workload, 'primaryEngineId', event.target.value)}>
          <option value="">Select primary</option>{engines.filter((engine) => engine.active).map((engine) => <option key={engine.id} value={engine.id}>{engine.name || engine.id}</option>)}
        </select></label>
        <label className={ui.label}>Ordered fallbacks (comma-separated IDs)<input className={'mt-1 ' + ui.input} value={(routes[workload]?.fallbackEngineIds || []).join(', ')} disabled={!routes[workload]?.automaticFallback}
          onChange={(event) => edit(workload, 'fallbackEngineIds', event.target.value.split(',').map((value) => value.trim()).filter(Boolean))} /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(routes[workload]?.automaticFallback)} onChange={(event) => edit(workload, 'automaticFallback', event.target.checked)} /> Automatic fallback</label>
      </div>)}
      <label className={ui.label}>Audit reason<input className={'mt-1 ' + ui.input} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
      <Button type="button" disabled={busy || reason.trim().length < 8} onClick={save}>Activate complete route set</Button>
      {message && <p role="status" className="mt-2 text-sm">{message}</p>}
      {state?.history?.length > 0 && <div className="mt-4"><h3 className="font-medium">Revision history</h3>{state.history.map((entry) => <p key={entry.id} className="text-sm">#{entry.revision} · {entry.active ? 'active' : entry.changeReason}
        {!entry.active && <button className="ml-2 underline" disabled={busy || reason.trim().length < 8} onClick={() => restore(entry.revision)}>Restore as new revision</button>}</p>)}</div>}
    </div>
  </section>
}
